import { useId, useState } from 'react'

import { Switch, type SwitchProps } from '@components/atoms/Switch'

import type { ComponentDoc, PropDocs } from '../types'

type SwitchOwnProps = Pick<
  SwitchProps,
  'checked' | 'defaultChecked' | 'onCheckedChange' | 'disabled' | 'required' | 'name' | 'value' | 'className' | 'ref'
>

const props: PropDocs<SwitchOwnProps> = {
  checked: { type: 'boolean', description: 'Whether it is on. Pass it with onCheckedChange to control the switch.' },
  defaultChecked: { type: 'boolean', default: 'false', description: 'Starting state when uncontrolled.' },
  onCheckedChange: { type: '(checked: boolean) => void', description: 'Called with the new state.' },
  disabled: {
    type: 'boolean',
    default: 'false',
    description: 'Stops it toggling, dims it and shows a not-allowed cursor.',
  },
  required: { type: 'boolean', default: 'false', description: 'For use inside a form.' },
  name: { type: 'string', description: 'Form field name; a hidden input carries the value when inside a form.' },
  value: { type: 'string', default: "'on'", description: 'Value submitted with the form when on.' },
  className: { type: 'string', description: 'Merged with cn, so it can override the defaults.' },
  ref: { type: 'Ref<HTMLButtonElement>', description: 'Forwarded to the button.' },
}

function Labelled({ label, hint, ...switchProps }: { label: string; hint?: string } & SwitchProps) {
  const id = useId()
  return (
    <div className="tw:flex tw:items-start tw:gap-3">
      <Switch id={id} aria-describedby={hint ? `${id}-hint` : undefined} {...switchProps} />
      <div>
        <label htmlFor={id} className="tw:m-0 tw:block tw:cursor-pointer tw:text-sm tw:font-medium tw:text-gray-900">
          {label}
        </label>
        {hint && (
          <p id={`${id}-hint`} className="tw:m-0 tw:text-xs tw:text-gray-true-500">
            {hint}
          </p>
        )}
      </div>
    </div>
  )
}

function Controlled() {
  const [on, setOn] = useState(true)
  return <Labelled label={on ? 'Coverage bars on' : 'Coverage bars off'} checked={on} onCheckedChange={setOn} />
}

const doc: ComponentDoc<SwitchProps> = {
  slug: 'switch',
  name: 'Switch',
  tier: 'atom',
  description:
    'An on/off setting that takes effect straight away, adapted from the shadcn/ui Radix switch. It needs a visible label linked with htmlFor (or aria-labelledby).',
  importStatement: "import { Switch } from '@components/atoms/Switch'",
  sourcePath: 'app/frontend/components/atoms/Switch.tsx',
  component: Switch,
  props,
  examples: [
    {
      title: 'With a label and a hint',
      render: () => (
        <Labelled label="Shift coverage bars" hint="Show how many volunteer spots are filled." defaultChecked />
      ),
      code: `const id = useId()

<Switch id={id} aria-describedby={\`\${id}-hint\`} checked={on} onCheckedChange={setOn} />
<label htmlFor={id}>Shift coverage bars</label>
<p id={\`\${id}-hint\`}>Show how many volunteer spots are filled.</p>`,
    },
    {
      title: 'Controlled',
      render: () => <Controlled />,
      code: `const [on, setOn] = useState(true)

<Switch id={id} checked={on} onCheckedChange={setOn} />`,
    },
    {
      title: 'Disabled',
      render: () => <Labelled label="Always show the nearest event" disabled defaultChecked />,
      code: `<Switch id={id} disabled defaultChecked />`,
    },
  ],
  usage: {
    do: [
      'Use it for a setting that applies as soon as it changes, like a preview option.',
      'Give it a visible <label htmlFor>, so clicking the label toggles it too.',
      'Put any hint in a paragraph linked with aria-describedby.',
    ],
    dont: [
      'Use it for a choice that only applies when a form is submitted. Use a Checkbox.',
      'Use it for one of several options. Use a RadioGroupField.',
    ],
  },
  a11y: [
    'Radix renders a button with role="switch" and aria-checked, toggled by Space and Enter.',
    'The off track (gray-cool-400) and the white thumb both keep 3:1 contrast against white (WCAG 1.4.11).',
    'The switch is 24×44px, meeting the 24px minimum target size (WCAG 2.5.8).',
  ],
}

export default doc
